import {of as observableOf,  Observable ,  BehaviorSubject } from 'rxjs';
import {map} from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Tenant, TenantDto, TenantDetailsDto, SimpleTenantDto } from 'shared/service-clients/tenant';
import { OrderManagementClient, MappingDetailsDto, MappingDetailsUpdateResultDto, ExportFileDto, ClientSpecialtyFileUploadDto, ClientDesignationFileUploadDto } from 'shared/service-clients/om-client';
import { FilterService } from './filter.service';
import { ClassificationValues } from 'dtp/common/classification-value';

// Also known as the Tenant Service
@Injectable()
export class ClientService {
  private clientIdStorageKey = 'samplicity-dtp-current-client';
  private clientIdChangeKey = 'samplicity-dtp-changed-client';
  private certificateMenu = 'certificates';
  private pharmacistMenu = 'pharmacists';

  constructor(
    private tenantClient: Tenant,
    private orderManagementClient: OrderManagementClient,
    private router: Router,
    private filterService: FilterService,
    private route: ActivatedRoute
  ) { }

  private notificationQueryStrings: Array<string> = [
    '?activated=',
    '?discarded=',
    '?created='
  ];

  private _selectedClientId: BehaviorSubject<string> = new BehaviorSubject(null);
  private _isClientActive: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _isExceedRuleValid: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _IsExceedQuantityByBrandProductValid: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _isGroupSplitProduct: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _isTemplateIdValid: BehaviorSubject<boolean> = new BehaviorSubject(null);
  private _selectedSalesOrderTypeClassificationValueId: BehaviorSubject<string> = new BehaviorSubject(null);
  private _orderStatusReasonTypeId: BehaviorSubject<string> = new BehaviorSubject(null);
  private _orderExternalNotes: BehaviorSubject<string> = new BehaviorSubject(null);

  get orderExternalNotesAsync(): Observable<string> {
    return this._orderExternalNotes.asObservable();
  }

  get selectedClientIdAsync(): Observable<string> {
    return this._selectedClientId.asObservable();
  }

  get selectedSalesOrderTypeAsync(): Observable<string> {
    return this._selectedSalesOrderTypeClassificationValueId.asObservable();
  }

  get orderStatusReasonTypeIdAsync(): Observable<string> {
    return this._orderStatusReasonTypeId.asObservable();
  }

  get isCurrentClientActiveAsync(): Observable<boolean> {
    return this._isClientActive.asObservable();
  }

  get isClientActive(): boolean {
    return this._isClientActive.value;
  }

  get isExceedRuleValid(): Observable<boolean> {
    return this._isExceedRuleValid.asObservable();
  }

  get isExceedQuantityByBrandProductValid(): Observable<boolean> {
    return this._IsExceedQuantityByBrandProductValid.asObservable();
  }

  get isGroupSplitProduct(): Observable<boolean> {
    return this._isGroupSplitProduct.asObservable();
  }

  get isTemplateIdValid(): Observable<boolean> {
    return this._isTemplateIdValid.asObservable();
  }

  setOrderExternalNotes(note: string): void {
    this._orderExternalNotes.next(note);
  }

  setSelectedClientId(id: string): void {
    this._selectedClientId.next(id);
  }

  setOrderStatusReasonTypeId(id: string): void {
    this._orderStatusReasonTypeId.next(id);
  }

  setSelectedOrderType(id: string): void {
    this._selectedSalesOrderTypeClassificationValueId.next(id);
  }

  setIsCurrentClientActive(isActive: boolean): void {
    this._isClientActive.next(isActive);
  }

  setIsExceedRuleValid(isValid: boolean): void {
    this._isExceedRuleValid.next(isValid);
  }

  setIsExceedQuantityByBrandProductRuleValid(isValid: boolean): void {
    this._IsExceedQuantityByBrandProductValid.next(isValid);
  }

  setIsGroupSplitProduct(isValid: boolean): void {
    this._isGroupSplitProduct.next(isValid);
  }

  setTemplateIdError(isValid: boolean): void {
    this._isTemplateIdValid.next(isValid);
  }

  getClientId(): number {
    const item = sessionStorage.getItem(this.clientIdStorageKey);
    this.setSelectedClientId(item);
    return Number(item);
  }

  setClientChange(): void {
    localStorage.setItem(this.clientIdChangeKey, JSON.stringify(true));
  }

  getSimpleClients(tenantIds, getAllTenants): Observable<SimpleTenantDto[]> {
    return this.tenantClient.getSimpleTenants(tenantIds, getAllTenants).pipe(map((x) => {
      return x.result;
    }, (e: any) => {
      console.log(e);
    }));
  }

  getActiveClients(includeActiveOnly = true): Observable<SimpleTenantDto[]> {
    return this.tenantClient.getActiveSimpleTenants(includeActiveOnly).pipe(map((x) => {
      return x.result;
    }, (e: any) => {
      console.log(e);
    }));
  }

  getCurrentClient(): Observable<TenantDto> {
    const clientId = this.getClientId();
    return this.getClientById(clientId);
  }

  isCurrentClientActive(client: TenantDto): boolean {
    if (client.tenantModules.length > 0) {
      const isActive = client.tenantModules.some(a => a.tenantModuleStatusClassificationValueId === ClassificationValues.TENANT_MODULE_STATUS_ACTIVE)
      return isActive;
    } else {
      return false;
    }
  }

  isCurrentClientActiveOrNew(client: TenantDto): boolean {
    if (client.tenantModules.length === 1) {
      const status = client.tenantModules[0].tenantModuleStatusClassificationValueId;
      return status === ClassificationValues.TENANT_MODULE_STATUS_ACTIVE
        || status === ClassificationValues.TENANT_MODULE_STATUS_NEW;
    } else {
      return false;
    }
  }

  private getClientById(clientId: number): Observable<TenantDto> {
    return this.tenantClient.getTenantInfoById(clientId).pipe(map(x => {
      return x.result;
    }, (e: any) => {
      console.log(e);
    }));
  }

  getCurrentClientDetails(): Observable<TenantDetailsDto> {
    const clientId = this.getClientId();
    return this.getClientDetailsById(clientId);
  }

  getCurrentClientSpecialtyDetails(): Observable<MappingDetailsDto[]> {
    return this.orderManagementClient.getCurrentClientSpecialtyMappings().pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  getCurrentClientExportData(exportTypeId: string): Observable<ExportFileDto> {
    const clientId = this.getClientId();
    return this.orderManagementClient.getCurrentClientExportData(exportTypeId).pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  saveCurrentClientSpecialties(updateDtos: MappingDetailsDto[]): Observable<MappingDetailsUpdateResultDto> {
    return this.orderManagementClient.saveClientSpecialtyMappings(updateDtos).pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  uploadCurrentClientSpecialtyFile(uploadDto: ClientSpecialtyFileUploadDto): Observable<ClientSpecialtyFileUploadDto> {
    return this.orderManagementClient.uploadClientSpecialtyMappingFile(uploadDto).pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  getCurrentClientDesignationDetails(): Observable<MappingDetailsDto[]> {
    return this.orderManagementClient.getCurrentClientDesignationMappings().pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  saveCurrentClientDesignations(updateDtos: MappingDetailsDto[]): Observable<MappingDetailsUpdateResultDto> {
    return this.orderManagementClient.saveClientDesignationMappings(updateDtos).pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  uploadCurrentClientDesignationFile(uploadFileDto: ClientDesignationFileUploadDto): Observable<ClientDesignationFileUploadDto> {
    return this.orderManagementClient.uploadClientDesignationMappingFile(uploadFileDto).pipe(map(x => {
      return x.result;
    },
      (e: any) => {
        console.log(e);
      }));
  }

  private getClientDetailsById(clientId: number): Observable<TenantDetailsDto> {
    if (clientId === 0) {
      return observableOf(null);
    }
    return this.tenantClient.getTenantDetails(clientId).pipe(map(x => {
      return x.result;
    }, (e: any) => {
      console.log(e);
    }));
  }

  setClientId(clientId) {
    this.setSelectedClientId(clientId);
    sessionStorage.setItem(this.clientIdStorageKey, clientId);
  }

  clearClientId() {
    sessionStorage.removeItem(this.clientIdStorageKey);
    this.setSelectedClientId(null);
  }

  changeClient(clientId) {
    const currentClientId = this.getClientId();
    if (currentClientId !== Number(clientId)) {
      this.setClientId(clientId);
      this.getClientDetailsById(clientId).subscribe(x => {
        let redirectUrl = this.router.url;
        let dtp = x.tenantModules.some(a => a.moduleId == ClassificationValues.TENANT_MODULE_TYPE_DTP);
        let fg = x.tenantModules.some(a => a.moduleId == ClassificationValues.TENANT_MODULE_TYPE_FGPORTAL);
        if ((redirectUrl.indexOf(this.certificateMenu) > 0 || (redirectUrl.indexOf(this.pharmacistMenu) > 0)) && dtp && !fg) {
          redirectUrl = "/" + this.getClientId().toString() + "/dashboard";
        }
        else {
          let urlSegments = redirectUrl.split('/');
          urlSegments[1] = this.getClientId().toString();
          redirectUrl = urlSegments.join('/');
        }

        for (const nqs of this.notificationQueryStrings) {
          const i = redirectUrl.indexOf(nqs);
          if (i >= 0) {
            redirectUrl = redirectUrl.substring(0, i);
          }
        }

        this.filterService.clearAllFilter();
        this.router.navigate([redirectUrl])
          .then(() => {
            this.setClientChange();
            location.reload();
          });
      });
    }
  }

  deactivateClientForDtp(clientId: number): Observable<TenantDetailsDto> {
    return this.tenantClient.deactivateTenantForDtp(clientId).pipe(map(x => {
      return x.result;
    }));
  }

}

export class Item {
  value: any;
  label: string;

  constructor(value, label) {
    this.value = value;
    this.label = label;
  }
}
